import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import styled from "styled-components";
import { FiHome, FiMapPin, FiUsers, FiPlus, FiMenu, FiX, FiLogOut, FiChevronDown } from "react-icons/fi";
import Logo from "./Logo";

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.bg};
`;

const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space(4)};
  padding: ${({ theme }) => `${theme.space(3)} ${theme.space(6)}`};
  background: rgba(250, 249, 245, 0.85);
  backdrop-filter: saturate(140%) blur(10px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  ${({ theme }) => theme.media.md} {
    padding: ${({ theme }) => `${theme.space(3)} ${theme.space(4)}`};
  }
`;

const Brand = styled.button`
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(1)};

  ${({ theme }) => theme.media.md} {
    display: none;
  }
`;

const NavItem = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: color 0.15s ease, background 0.15s ease;

  svg { width: 16px; height: 16px; }

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  &.active {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.accentSoft};
  }
`;

const UserCluster = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(2)};

  ${({ theme }) => theme.media.md} {
    display: none;
  }
`;

const UserMenu = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px 6px 6px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  transition: background 0.15s ease;

  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }

  svg { width: 14px; height: 14px; color: ${({ theme }) => theme.colors.textMuted}; }
`;

const Avatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.accent};
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 220px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  padding: ${({ theme }) => theme.space(2)};
  z-index: 40;
`;

const DropdownMeta = styled.div`
  padding: ${({ theme }) => `${theme.space(2)} ${theme.space(3)}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.space(2)};
  .name { font-size: 13px; font-weight: 600; }
  .email { font-size: 12px; color: ${({ theme }) => theme.colors.textMuted}; }
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: ${({ theme }) => `${theme.space(2)} ${theme.space(3)}`};
  background: transparent;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;

  &:hover { background: ${({ theme }) => theme.colors.bgAlt}; }
  svg { width: 16px; height: 16px; color: ${({ theme }) => theme.colors.textMuted}; }
`;

const Hamburger = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  ${({ theme }) => theme.media.md} {
    display: inline-flex;
  }
  svg { width: 18px; height: 18px; }
`;

const Drawer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  display: ${({ open }) => (open ? "flex" : "none")};
  flex-direction: column;
  background: rgba(31, 30, 29, 0.4);
  backdrop-filter: blur(2px);
`;

const DrawerPanel = styled.div`
  margin-left: auto;
  width: min(320px, 90vw);
  height: 100%;
  background: ${({ theme }) => theme.colors.surface};
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.space(5)};
  gap: ${({ theme }) => theme.space(4)};
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DrawerNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: ${({ theme }) => theme.space(2)};
`;

const DrawerNavItem = styled(NavItem)`
  font-size: 16px;
  padding: 12px 14px;
  svg { width: 18px; height: 18px; }
`;

const DrawerFooter = styled.div`
  margin-top: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: ${({ theme }) => theme.space(4)};
`;

const Main = styled.main`
  flex: 1;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.space(8)} ${theme.space(6)} ${theme.space(10)}`};

  ${({ theme }) => theme.media.md} {
    padding: ${({ theme }) => `${theme.space(5)} ${theme.space(4)} ${theme.space(8)}`};
  }
`;

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: FiHome, end: true },
  { to: "/sites", label: "Sites", icon: FiMapPin },
  { to: "/users", label: "Customers", icon: FiUsers },
  { to: "/sites/add", label: "Add site", icon: FiPlus },
];

const AppShell = ({ children }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest?.("[data-user-menu]")) setMenuOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const fullName = auth.user?.profile?.name || "Admin";
  const email = auth.user?.profile?.email || "";
  const initials = fullName
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("");

  const onSignOut = () => {
    auth.removeUser();
    window.history.replaceState({}, document.title, "/");
    navigate("/");
  };

  return (
    <Shell>
      <TopBar>
        <Brand onClick={() => navigate("/dashboard")} aria-label="Go to dashboard">
          <Logo size={20} />
        </Brand>

        <NavLinks aria-label="Primary">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavItem key={to} to={to} end={end}>
              <Icon /> {label}
            </NavItem>
          ))}
        </NavLinks>

        <UserCluster>
          <UserMenu data-user-menu>
            <UserButton onClick={() => setMenuOpen((v) => !v)} aria-haspopup="menu" aria-expanded={menuOpen}>
              <Avatar>{initials || "A"}</Avatar>
              {fullName.split(" ")[0]}
              <FiChevronDown />
            </UserButton>
            {menuOpen && (
              <Dropdown role="menu">
                <DropdownMeta>
                  <div className="name">{fullName}</div>
                  {email && <div className="email">{email}</div>}
                </DropdownMeta>
                <DropdownItem onClick={onSignOut} role="menuitem">
                  <FiLogOut /> Sign out
                </DropdownItem>
              </Dropdown>
            )}
          </UserMenu>
        </UserCluster>

        <Hamburger
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          aria-expanded={drawerOpen}
        >
          <FiMenu />
        </Hamburger>
      </TopBar>

      <Drawer open={drawerOpen} onClick={(e) => e.target === e.currentTarget && setDrawerOpen(false)}>
        <DrawerPanel>
          <DrawerHeader>
            <Logo size={20} />
            <Hamburger onClick={() => setDrawerOpen(false)} aria-label="Close menu">
              <FiX />
            </Hamburger>
          </DrawerHeader>
          <DrawerNav>
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <DrawerNavItem key={to} to={to} end={end}>
                <Icon /> {label}
              </DrawerNavItem>
            ))}
          </DrawerNav>
          <DrawerFooter>
            <DropdownMeta>
              <div className="name">{fullName}</div>
              {email && <div className="email">{email}</div>}
            </DropdownMeta>
            <DropdownItem onClick={onSignOut}>
              <FiLogOut /> Sign out
            </DropdownItem>
          </DrawerFooter>
        </DrawerPanel>
      </Drawer>

      <Main>{children}</Main>
    </Shell>
  );
};

export default AppShell;
